/* eslint-disable @typescript-eslint/no-empty-function */
import pino from 'pino';

import { ecsFormat } from '@elastic/ecs-pino-format';

import type { Logger, LoggerOptions } from 'pino';

interface DefaultLoggerParams {
  type:
    | 'server_guard'
    | 'server_performance'
    | 'server_axios'
    | 'server_error'
    | 'client_axios'
    | 'client_guard'
    | 'client_error';
  url: string;
  custom?: Record<string, any>;
}

// 빈 로거 함수들을 가지는 객체를 생성합니다.
const noopLogger = {
  level: 'silent',
  fatal: () => {},
  error: () => {},
  warn: () => {},
  info: () => {},
  debug: () => {},
  trace: () => {},
  child: () => noopLogger,
  flush: () => {},
  // 다른 필요한 메서드들도 동일한 방식으로 추가할 수 있습니다.
} as unknown as Logger;

/**
 * 기본 로거를 가져오는 함수입니다.
 *
 * @param type - 로그 타입 (ex. guard, axios)
 * @param url - 요청 URL
 * @param custom - 기타 적재가 필요한 키 밸류 형태의 객체
 * @returns 기본 로거 객체
 * @example
 * const logger = getDefaultLogger({
 *   type: 'guard',
 *   url: href || 'unknown',
 *   custom: {
 *     hello: 'world'
 *   }
 * });
 * logger.info('my message');
 * logger.error({ console_error: error }, 'my error message');
 */
const getDefaultLogger = ({ type, url, custom = {} }: DefaultLoggerParams) => {
  const tag = process.env.KC_RELEASE_TAG ?? '';
  const ignoreLog = !tag || tag === 'local'; // local에서는 로그를 무시합니다.
  if (typeof window !== 'undefined') return noopLogger; // 클라이언트 사이드에서는 로거를 반환하지 않습니다.
  if (ignoreLog) return noopLogger;
  const env = process.env.KC_ENV;
  const iamEndpoint = process.env.KC_IAM_SSR_ENDPOINT ?? '';
  const isGovKr1 = iamEndpoint.includes('kr-gov-central-1');
  const ecsFormatObj = ecsFormat() as LoggerOptions<string, boolean>;
  const parentLogger = pino({
    ...ecsFormatObj,
    formatters: {
      ...ecsFormatObj.formatters,
      level(label: string) {
        return { console_log_level: label }; // 로그 레벨을 console_log_level로 추가
      },
    },
  });
  const refinedObj: Record<string, any> = {
    environment: env,
    log_type: type,
    tag: tag,
    url: url,
    ...custom,
  };

  const esIndex = `${env}-${isGovKr1 ? 'kr-gov-central-1' : 'kr-central-2'}-console`;

  // obj의 키가 console_ 로 시작하지 않으면 prefix를 붙여준다.
  Object.keys(refinedObj).forEach(key => {
    if (!key.startsWith('console_')) {
      refinedObj[`console_${key}`] = refinedObj[key];
      delete refinedObj[key];
    }
  });
  const childLogger = parentLogger.child({
    service_id: esIndex,
    ...refinedObj,
  });
  return childLogger;
};

export default getDefaultLogger;
